import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment
} from '@mui/material';

interface LoginScreenProps {
  onLogin: (email: string) => void;
}

const LoginScreen: React.FC<LoginScreenProps> = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [email, setEmail] = useState('');

  const getApiBaseUrl = () => {
    return window.location.hostname === 'localhost' ? 'http://localhost:5001' : 'https://api.delightrgext.xyz';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpSent && !username.trim()) return;
    if (otpSent && !otp.trim()) return;

    setLoading(true);
    setError('');
    
    try {
      if (!otpSent) {
        // Send OTP
        const fullEmail = `${username.trim()}@delightrg.com`;
        const response = await fetch(`${getApiBaseUrl()}/api/bot/auth/send-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username: fullEmail })
        });

        const result = await response.json();
        
        if (result.success) {
          setEmail(fullEmail);
          setOtpSent(true);
        } else {
          setError(result.message || 'Failed to send OTP');
        }
      } else {
        // Verify OTP
        const response = await fetch(`${getApiBaseUrl()}/api/bot/auth/verify-otp`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username: email, otp })
        });

        const result = await response.json();
        
        if (result.success) {
          onLogin(email);
        } else {
          setError(result.message || 'Invalid OTP');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Remove @delight.com if user tries to type it
    const cleanValue = value.replace('@delight.com', '');
    setUsername(cleanValue);
  };

  return (
    <>
      <style>
        {`
          @keyframes float1 {
            0%, 100% { transform: translate(0px, 0px); }
            50% { transform: translate(-20px, -15px); }
          }
          @keyframes float2 {
            0%, 100% { transform: translate(0px, 0px); }
            50% { transform: translate(-25px, -20px); }
          }
          @keyframes float3 {
            0%, 100% { transform: translate(0px, 0px); }
            50% { transform: translate(30px, -25px); }
          }
          @keyframes float4 {
            0%, 100% { transform: translate(0px, 0px); }
            50% { transform: translate(-15px, 30px); }
          }
          @keyframes float5 {
            0%, 100% { transform: translate(0px, 0px); }
            50% { transform: translate(20px, 15px); }
          }
          
          .bubble {
            position: absolute;
            border-radius: 50%;
            pointer-events: none;
          }
          
          .css-vufltt {
            width: 40px; height: 40px;
            background: rgba(255, 200, 150, 0.3);
            top: 10%; left: 8%;
            animation: 5s ease-in-out 0s infinite float1;
          }
          .css-10xctw0 {
            width: 60px; height: 60px;
            background: rgba(255, 180, 120, 0.25);
            top: 15%; right: 12%;
            animation: 6s ease-in-out 0s infinite float2;
          }
          .css-1764pss {
            width: 35px; height: 35px;
            background: rgba(200, 230, 255, 0.3);
            top: 25%; left: 15%;
            animation: 7s ease-in-out 1s infinite float3;
          }
          .css-1kfi0zh {
            width: 50px; height: 50px;
            background: rgba(255, 220, 180, 0.2);
            bottom: 20%; right: 20%;
            animation: 8s ease-in-out 0.5s infinite float4;
          }
          .css-trpzbb {
            width: 45px; height: 45px;
            background: rgba(180, 255, 200, 0.25);
            top: 35%; right: 25%;
            animation: 5.5s ease-in-out 2s infinite float5;
          }
          .css-hjp7s7 {
            width: 30px; height: 30px;
            background: rgba(255, 160, 200, 0.3);
            bottom: 30%; left: 10%;
            animation: 6.5s ease-in-out 1.5s infinite float1;
          }
          .css-1gf46q6 {
            width: 55px; height: 55px;
            background: rgba(200, 180, 255, 0.2);
            top: 45%; left: 25%;
            animation: 7.5s ease-in-out 0.8s infinite float2;
          }
          .css-1dec2ub {
            width: 38px; height: 38px;
            background: rgba(255, 240, 150, 0.28);
            top: 60%; right: 15%;
            animation: 6.8s ease-in-out 2.2s infinite float3;
          }
          .css-p3p5q7 {
            width: 42px; height: 42px;
            background: rgba(150, 255, 180, 0.25);
            bottom: 15%; left: 30%;
            animation: 5.8s ease-in-out 1.8s infinite float4;
          }
          .css-ka6zot {
            width: 48px; height: 48px;
            background: rgba(255, 190, 160, 0.22);
            top: 20%; left: 45%;
            animation: 7.2s ease-in-out 0.3s infinite float5;
          }
          .css-1u7dk59 {
            width: 33px; height: 33px;
            background: rgba(180, 200, 255, 0.3);
            bottom: 40%; right: 8%;
            animation: 6.3s ease-in-out 2.5s infinite float1;
          }
          .css-156vbs0 {
            width: 52px; height: 52px;
            background: rgba(255, 210, 170, 0.24);
            top: 70%; left: 18%;
            animation: 8.2s ease-in-out 1.2s infinite float2;
          }
          .css-iujj9d {
            width: 36px; height: 36px;
            background: rgba(200, 255, 220, 0.26);
            top: 55%; right: 35%;
            animation: 5.3s ease-in-out 0.7s infinite float3;
          }
          .css-gledgx {
            width: 44px; height: 44px;
            background: rgba(255, 170, 190, 0.23);
            bottom: 25%; left: 40%;
            animation: 7.8s ease-in-out 2.8s infinite float4;
          }
          .css-1y8rw32 {
            width: 39px; height: 39px;
            background: rgba(190, 180, 255, 0.27);
            top: 80%; right: 30%;
            animation: 6.7s ease-in-out 1.7s infinite float5;
          }
        `}
      </style>
      <Box className="css-1p3nuyd" sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        backgroundColor: 'rgb(246, 240, 215)'
      }}>
      {/* Decorative background elements */}
      <Box className="css-vufltt bubble" />
      <Box className="css-10xctw0 bubble" />
      <Box className="css-1764pss bubble" />
      <Box className="css-1kfi0zh bubble" />
      <Box className="css-trpzbb bubble" />
      <Box className="css-hjp7s7 bubble" />
      <Box className="css-1gf46q6 bubble" />
      <Box className="css-1dec2ub bubble" />
      <Box className="css-p3p5q7 bubble" />
      <Box className="css-ka6zot bubble" />
      <Box className="css-1u7dk59 bubble" />
      <Box className="css-156vbs0 bubble" />
      <Box className="css-iujj9d bubble" />
      <Box className="css-gledgx bubble" />
      <Box className="css-1y8rw32 bubble" />
      
      <Paper
        elevation={1}
        className="css-dzx54q"
        sx={{
          color: 'rgba(0, 0, 0, 0.87)',
          padding: '40px',
          maxWidth: '450px',
          width: '100%',
          borderRadius: '16px',
          boxShadow: 'rgba(0, 0, 0, 0.3) 0px 20px 60px, rgba(255, 255, 255, 0.5) 0px 0px 0px 1px inset',
          background: 'linear-gradient(135deg, rgba(255, 220, 180, 0.4), rgba(200, 230, 255, 0.4))',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          position: 'relative',
          transform: 'translateZ(50px) rotateX(2deg)',
          transformStyle: 'preserve-3d',
          transition: '0.3s',
          zIndex: 1
        }}
      >
        <Typography variant="h4" component="h4" className="css-odye7q" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold' }}>
          TrueView
        </Typography>
        
        <Typography variant="body2" className="css-l34n4u" sx={{ mb: 3, textAlign: 'center', color: 'text.secondary' }}>
          {otpSent ? `OTP sent to ${email}` : 'Sign in to your account'}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} className="css-pdq02v">
          {!otpSent ? (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Email Address"
                value={username}
                onChange={handleUsernameChange}
                required
                inputProps={{ maxLength: 50 }}
                variant="outlined"
                className="css-1xtqkva"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="body1" className="css-ipzzvp" sx={{ color: 'text.secondary' }}>
                        @delightrg.com
                      </Typography>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          ) : (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                inputProps={{ maxLength: 6 }}
                variant="outlined"
                sx={{ textAlign: 'center' }}
              />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            className="css-1s0yfk0"
            sx={{ py: 1.5 }}
          >
            {loading ? (otpSent ? 'Verifying...' : 'Sending OTP...') : (otpSent ? 'Verify OTP' : 'Get OTP')}
          </Button>
        </Box>
      </Paper>
    </Box>
    </>
  );
};

export default LoginScreen;